import { SyncRequestConfig, useStorytellClient } from "@storytell/client";
import type { Asset, Concept, Label, Realm, View, Workspace } from "@storytell/types";
import Dexie, { type EntityTable } from "dexie";
import { createMemo, createRenderEffect, onCleanup, onMount } from "solid-js";
import { createStore } from "solid-js/store";
import { isServer } from "solid-js/web";
import { computeSyncRelationships } from "./computeSyncRelationships";
import { createEntitySyncManager } from "./createEntitySyncManager";

export type DbEntities = {
  assets: Asset & { userId: string };
  labels: Label & { userId: string };
  views: View & { userId: string };
  workspaces: Workspace & { userId: string };
  realms: Realm & { userId: string };
  concepts: Concept & { userId: string };
  // TODO: @andi users
};

type SyncStoreDb = {
  [Key in keyof DbEntities]: EntityTable<DbEntities[Key], "id">;
};

type DexieWithTables = Dexie & SyncStoreDb;

const initLocalDb = () => {
  if (isServer) return null;
  const db = new Dexie("synStore:v1");
  db.version(1).stores({
    assets: "id",
    concepts: "id",
    labels: "id",
    views: "id",
    workspaces: "id",
    realms: "id",
    users: "id",
  });
  return db as DexieWithTables;
};

export const createSyncService = (deps: { userId: () => string | undefined; token: () => string | undefined }) => {
  const userId = createMemo(() => deps.userId());

  const db = initLocalDb();

  const assets = createEntitySyncManager<Asset & { userId: string }>(db?.assets, (asset) => [
    asset.fileName,
    asset.displayName,
  ]);
  const labels = createEntitySyncManager<Label & { userId: string }>(db?.labels, (label) => [label.name]);
  const views = createEntitySyncManager<View & { userId: string }>(db?.views);
  const workspaces = createEntitySyncManager<Workspace & { userId: string }>(db?.workspaces, (workspace) => [
    workspace.name,
  ]);
  const realms = createEntitySyncManager<Realm & { userId: string }>(db?.realms);
  const concepts = createEntitySyncManager<Concept & { userId: string }>(db?.concepts, (concept) => [concept.name]);

  const entities = [assets, labels, views, workspaces, realms, concepts];

  const syncRequest = async () => {
    const id = userId();
    if (!id) return;

    const client = useStorytellClient();
    const request = SyncRequestConfig.withBearerToken(deps.token());
    const response = await client.fetch(request, undefined, undefined);

    if (response.isErr()) {
      console.error("Failed to sync request", response.error);
      return;
    }
    const { value } = response;

    const mapRecordsToArrayWithUserId = <T extends { id: string }, R extends Record<string, T>>(entity: R) =>
      Object.values(entity).map((record) => ({ ...record, userId: id }));

    assets.setBulk(mapRecordsToArrayWithUserId(value.assets));
    labels.setBulk(mapRecordsToArrayWithUserId(value.labels));
    views.setBulk(mapRecordsToArrayWithUserId(value.views));
    workspaces.setBulk(mapRecordsToArrayWithUserId(value.workspaces));
    realms.setBulk(mapRecordsToArrayWithUserId(value.realms));
    concepts.setBulk(mapRecordsToArrayWithUserId(value.concepts));
  };

  // Hydrate the entities when the user changes
  createRenderEffect(() => {
    const id = userId();
    if (!id) return;
    for (const entity of entities) {
      entity.hydrate(id);
    }

    // Call sync after user changes
    syncRequest();
  });

  // Periodic sync
  onMount(() => {
    const interval = setInterval(() => {
      syncRequest();
    }, 1000 * 60); // 1 minute

    onCleanup(() => {
      clearInterval(interval);
    });
  });

  const computedRelationships = computeSyncRelationships({ assets, concepts });

  return {
    assets,
    concepts,
    labels,
    views,
    workspaces,
    realms,
    ...computedRelationships,
  };
};
